import type { PropsWithChildren } from "react";
import { useCallback, useEffect, useRef } from "react";
import { styled } from "styled-components";

import { Spinner } from "common/components/Spinner";

const StyledContainer = styled.div`
  width: 100%;
`;

const StyledFooter = styled.div<{ hasNextPage: boolean }>`
  display: flex;
  justify-content: center;
  align-items: center;
  height: ${({ hasNextPage }) => (hasNextPage ? 100 : 0)}px;
  width: 100%;
  text-align: center;
`;

const StyledSpinner = styled(Spinner)`
  width: 20px;
  height: 20px;
  color: ${({ theme }) => theme.colors.fillPrimary};
`;

type InfinityLoaderProps = PropsWithChildren & {
  className?: string;
  dataTestId?: string;
  hasNextPage: boolean;
  isPending: boolean;
  loadNextPage?: (() => void) | (() => Promise<void>);
};

export const InfinityLoader = ({
  children,
  className,
  dataTestId,
  hasNextPage,
  isPending,
  loadNextPage,
}: InfinityLoaderProps) => {
  const ref = useRef<HTMLDivElement>(null);

  const handleIntersection = useCallback(
    async (entry: IntersectionObserverEntry) => {
      if (entry.isIntersecting && loadNextPage) {
        await loadNextPage();
      }
    },
    [loadNextPage],
  );

  useEffect(() => {
    const element = ref.current;
    if (!element || !hasNextPage || isPending) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        void handleIntersection(entry);
      },
      { root: null, rootMargin: "0px", threshold: 0.1 },
    );
    observer.observe(element);
    return () => {
      observer.unobserve(element);
    };
  }, [loadNextPage, hasNextPage, isPending, handleIntersection]);

  if (!loadNextPage) return children;

  return (
    <StyledContainer data-test-id={dataTestId} className={className}>
      {children}
      <StyledFooter ref={ref} hasNextPage={hasNextPage}>
        {/* TODO: check that all uses of `InfinityLoader` use `isPending` */}
        {isPending && <StyledSpinner />}
      </StyledFooter>
    </StyledContainer>
  );
};
